import { useLazyQuery } from "@apollo/client";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Location } from "../../__generated__/graphql";
import { GET_COMPANY } from "../../features/graphql/query/company";
import { GET_LOCATION } from "../../features/graphql/query/location/GetLocation";

export interface LocationContextType {
    location: LocationDataType,
    setLocation: (location: LocationDataType) => void
}
export type LocationDataType = Location | null;
export const LocationContext = React.createContext<LocationContextType | null>(null);

LocationContext.displayName = "LocationContext";

export interface LocationProviderProps {
    children: React.ReactNode
}

export const LocationProvider = ({ children }: LocationProviderProps) => {
    const [location, setLocation] = useState<LocationDataType | null>(null);

    const memorizedValue = useMemo(() => ({
        location, setLocation
    }), [location]);
    return (
        <LocationContext.Provider value={memorizedValue}>
            {children}
        </LocationContext.Provider>
    )
}

export const useLocationContext = () => {
    const context = useContext(LocationContext);
    if (!context)
        throw new Error('Call made outside provider. You must use useSelectPersonContext within the SelectedPersonProvider provider.');
    return context;
}

interface SelectedLocation {
    loading: boolean,
    location: LocationDataType,
    setLocation: (location: LocationDataType) => void,
    fetchLocation: (id: string) => void
}

export const useLocation = (id?: string | null): SelectedLocation => {
    const { location, setLocation } = useLocationContext();
    const [fetch, { data, loading }] = useLazyQuery(GET_LOCATION, {
        notifyOnNetworkStatusChange: true
    });
    const fetchLocation = (id: string) => {
        fetch({ variables: { id } })
    };

    useEffect(() => {
        if (!id || location) return;
        fetchLocation(id);
    }, [id, location]);

    useEffect(() => {
        if (loading || !data) return;
        const _location = data?.location
        setLocation(_location)
    }, [loading, data])

    return { loading, location, setLocation, fetchLocation }
}
